import { FC, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Dialog,
  DialogContent,
  DialogTitle,
  Typography
} from "@mui/material";
import { Broker, TradingCounterParty } from "../../../api/Api";
import { CounterpartyForm } from "../../tradingCounterparties/counterpartyForm";
import { BrokerForm } from "../../brokers/brokerForm";
import { useUpdateCounterparty } from "../../tradingCounterparties/dataHooks/counterpartyQueries";
import { useUpdateBroker } from "../../brokers/dataHooks/brokerQueries";
import { Progress } from "../../../components/lifecycle/departmentProgress/utils";
import {
  answersComplete,
  MergerAnswers,
  pickAnswers,
  toLifecycleAnswers
} from "../marketAccessFields";
import { MergerSection } from "../mergerSection";
import { MergerAttachments } from "../mergerAttachments";
import {
  applyBrokerRequest,
  applyCounterpartyRequest,
  brokerToRequest,
  counterpartyToRequest
} from "../entityMapping";

type Props = {
  open: boolean;
  onClose: () => void;
} & (
  | { kind: "counterparty"; entity: TradingCounterParty }
  | { kind: "broker"; entity: Broker }
);

/**
 * How Compliance edits a merger, the same way Market Access edits a counterparty or broker: the
 * book's create form (base fields and VP) plus the merger section (description, Market Access
 * answers, documents), saved as one document. Type is fixed after create, so there is no picker.
 */
export const EditMergerDialog: FC<Props> = (props) => {
  const { open, onClose, kind, entity } = props;

  const [description, setDescription] = useState("");
  const [answers, setAnswers] = useState<MergerAnswers>({});

  useEffect(() => {
    if (open) {
      setDescription(entity.mergerDescription ?? "");
      setAnswers(pickAnswers(kind, entity.marketAccessLifecycle));
    }
  }, [open, kind, entity]);

  // Both called unconditionally (hooks); only the one for this kind is used.
  const updateCounterparty = useUpdateCounterparty({ onSuccess: onClose });
  const updateBroker = useUpdateBroker({ onSuccess: onClose });
  const update = kind === "counterparty" ? updateCounterparty : updateBroker;

  // Memoised: the forms reset their fields whenever initialValues changes identity.
  const counterpartyInitialValues = useMemo(
    () =>
      kind === "counterparty"
        ? counterpartyToRequest(entity as TradingCounterParty)
        : null,
    [kind, entity]
  );
  const brokerInitialValues = useMemo(
    () => (kind === "broker" ? brokerToRequest(entity as Broker) : null),
    [kind, entity]
  );

  const mergerIncomplete =
    !description.trim() || !answersComplete(kind, answers);

  const mergerFields = {
    mergerDescription: description.trim(),
    marketAccessLifecycle: {
      ...entity.marketAccessLifecycle,
      ...toLifecycleAnswers(kind, answers)
    }
  };

  const extraSection = (
    <MergerSection
      kind={kind}
      description={description}
      onDescriptionChange={setDescription}
      answers={answers}
      onAnswerChange={(key, value) =>
        setAnswers((current) => ({ ...current, [key]: value }))
      }
      attachments={
        entity.id ? (
          <MergerAttachments kind={kind} entityId={entity.id} canUpload />
        ) : null
      }
    />
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Typography variant="h5" component="span">
          Edit merger
        </Typography>
      </DialogTitle>
      <DialogContent>
        {update.isError && (
          <Alert severity="error" sx={{ marginTop: 1 }}>
            We couldn&apos;t save the merger. Please try again.
          </Alert>
        )}
        {props.kind === "counterparty" ? (
          <CounterpartyForm
            initialValues={counterpartyInitialValues}
            hideComment
            extraSection={extraSection}
            submitDisabled={mergerIncomplete}
            submitting={updateCounterparty.isPending}
            submitLabel="Save"
            onCancel={onClose}
            onSubmit={(request) => {
              const tradeCaptureNotNeeded =
                answers.tradeCapture === "not_needed" &&
                props.entity.techLifecycle?.complete !== true;
              updateCounterparty.mutate({
                ...applyCounterpartyRequest(props.entity, request),
                ...mergerFields,
                // What the Market Access dialog's Complete does: no trade capture, no Tech work.
                ...(tradeCaptureNotNeeded
                  ? {
                      progress: {
                        ...props.entity.progress,
                        tech: Progress.Completed
                      },
                      techLifecycle: {
                        ...props.entity.techLifecycle,
                        complete: true
                      }
                    }
                  : {})
              });
            }}
          />
        ) : (
          <BrokerForm
            initialValues={brokerInitialValues}
            hideComment
            extraSection={extraSection}
            submitDisabled={mergerIncomplete}
            submitting={updateBroker.isPending}
            submitLabel="Save"
            onCancel={onClose}
            onSubmit={(request) =>
              updateBroker.mutate({
                ...applyBrokerRequest(props.entity, request),
                ...mergerFields
              })
            }
          />
        )}
      </DialogContent>
    </Dialog>
  );
};
